import { useEffect, useRef, useState, type MouseEvent } from "react";
import { useVideoLuminosity } from "../hooks/useVideoLuminosity";
import { Play, VolumeX } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { api } from "@/lib/axios";

interface MemoryVideoPlayerProps {
  videoUrl: string;
  memorySlug?: string;
  onLogoColorChange?: (color: "white" | "black") => void;
  onVideoEnd?: () => void;
}

export function MemoryVideoPlayer({
  videoUrl,
  memorySlug,
  onLogoColorChange,
  onVideoEnd,
}: MemoryVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const logoColor = useVideoLuminosity(videoRef);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  // Analytics: track first play only (not every loop iteration)
  const hasTrackedFirstPlay = useRef(false);
  const trackedMilestones = useRef<Set<number>>(new Set());
  const hasNotifiedVideoEnd = useRef(false);
  const hasReached95Percent = useRef(false);
  const hasCountedView = useRef(false);

  // Notify parent component of logo color changes
  if (onLogoColorChange) {
    onLogoColorChange(logoColor);
  }



  const [autoplaySucceeded, setAutoplaySucceeded] = useState(false);
  const [manualPlayRequested, setManualPlayRequested] = useState(false);

  const handleManualPlay = (e: MouseEvent) => {
    e.stopPropagation();
    setManualPlayRequested(true);

    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    video.play().catch(() => {
      // If the user taps play and it still fails, keep play visible.
      setAutoplaySucceeded(false);
    });
  };

  const handleUnmute = (e: MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    video.muted = false;
  };

  const countVideoView = async () => {
    if (!memorySlug || hasCountedView.current) return;
    hasCountedView.current = true;

    try {
      await api.get(`/v1/memories/${memorySlug}/video`);
    } catch (error) {
      console.error('Error counting video view:', error);
    }
  };

  const [autoplayCheckDone, setAutoplayCheckDone] = useState(false);

  // Analytics: track video progress milestones
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !memorySlug) return;

    const handleTimeUpdate = () => {
      if (!video.duration || video.duration === Infinity) return;
      
      const percent = Math.floor((video.currentTime / video.duration) * 100);
      const milestones = [25, 50, 75, 100];
      
      for (const milestone of milestones) {
        if (percent >= milestone && !trackedMilestones.current.has(milestone)) {
          trackedMilestones.current.add(milestone);
          trackEvent('video_progress', { memory_slug: memorySlug, percent: milestone });
          
          // Track video_complete at 100%
          if (milestone === 100) {
            trackEvent('video_complete', { memory_slug: memorySlug });
          }
        }
      }

      if (percent >= 95 && !hasReached95Percent.current) {
        hasReached95Percent.current = true;
        if (!hasNotifiedVideoEnd.current) {
          hasNotifiedVideoEnd.current = true;
          onVideoEnd?.();
        }
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [memorySlug]);

  useEffect(() => {
    // Ensure CORS is configured before setting the source (iOS Safari is picky).
    const video = videoRef.current;
    if (video) {
      video.crossOrigin = "anonymous";
      if (video.currentSrc !== videoUrl) {
        video.src = videoUrl;
        video.load();
      }
    }

    // Give iOS a short moment to start autoplay before showing any UI.
    // If autoplay works (even in Low Power Mode), Play should never flash.
    setAutoplayCheckDone(false);
    const timeoutId = window.setTimeout(() => {
      setAutoplayCheckDone(true);
    }, 900);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [videoUrl]);

  // Spec:
  // - Play shows if autoplay did NOT start
  // - Mute shows if autoplay DID start (playing+muted)
  // - Never show either until the initial autoplay check completes
  const shouldShowPlayOverlay = autoplayCheckDone && !autoplaySucceeded && !isPlaying;
  const shouldShowMuteOverlay = autoplayCheckDone && autoplaySucceeded && !manualPlayRequested && isPlaying && isMuted;

  return (
    <div className="fixed inset-0 bg-black z-0 flex items-center justify-center">
        {/* Video Container - Fullscreen on mobile, limited on desktop */}
        <div className="w-full h-full md:w-auto md:h-auto md:max-w-xl md:max-h-[80vh] relative">
            <video
                ref={videoRef}
                autoPlay
                poster={videoUrl + '#t=0.001'}
                loop
                muted
                playsInline
                controls={false}
                preload="auto"
                className="w-full h-full md:h-auto object-cover"
                aria-label="Recuerdo de tu visita"
            onPlay={() => {
              setIsPlaying(true);

              // Count video view on first play
              countVideoView();

              // Analytics: track first play only
              if (!hasTrackedFirstPlay.current && memorySlug) {
                hasTrackedFirstPlay.current = true;
                trackEvent('video_play', { memory_slug: memorySlug });
              }

              // If playback starts while muted and the user didn't press our Play button,
              // treat it as autoplay success.
              if (!manualPlayRequested && videoRef.current?.muted) {
                setAutoplaySucceeded(true);
              }
            }}
            onPlaying={() => {
              setIsPlaying(true);

              if (!manualPlayRequested && videoRef.current?.muted) {
                setAutoplaySucceeded(true);
              }
            }}
            onPause={() => {
              setIsPlaying(false);
              
              // Analytics: track pause with current time
              if (memorySlug) {
                trackEvent('video_pause', { 
                  memory_slug: memorySlug, 
                  current_time: videoRef.current?.currentTime 
                });
              }
            }}
            onEnded={() => {
              setIsPlaying(false);
            }}
            onVolumeChange={() => {
              setIsMuted(!!videoRef.current?.muted);
            }}
            onLoadedMetadata={() => {
              // Initialize muted state from the element
              setIsMuted(!!videoRef.current?.muted);
            }}
        >
            <track kind="captions" />
        </video>
        
        {/* Action Overlays (never overlap)
            - Play: autoplay did not start
            - Mute: autoplay started (playing+muted)
        */}
        {shouldShowPlayOverlay && (
          <div
            className="absolute inset-0 flex items-center justify-center z-20 bg-black/20"
            onClick={handleManualPlay}
          >
            <div className="bg-white/20 backdrop-blur-sm p-6 rounded-full border border-white/40 cursor-pointer animate-pulse hover:bg-white/30 transition-all">
              <Play className="w-12 h-12 text-white fill-white" />
            </div>
          </div>
        )}

        {shouldShowMuteOverlay && (
          <div
            className="absolute inset-0 flex items-center justify-center z-20 bg-black/20"
            onClick={handleUnmute}
          >
            <div className="bg-white/20 backdrop-blur-sm p-6 rounded-full border border-white/40 cursor-pointer animate-pulse hover:bg-white/30 transition-all">
              <VolumeX className="w-12 h-12 text-white" />
              <span className="sr-only">Activar sonido</span>
            </div>
          </div>
        )}
        </div>
    </div>
  );
}
